// Scheduled tasks — ⋯ → Scheduled tasks.
//
// What the box will say to a chat later, on its own. The scheduler has been
// firing for days with no way to see what was armed; this is that view.
//
// Design decisions carried over from six preview rounds with Julian:
//
//  - ONE ROW IS ONE SCHEDULE. "Weekly for four weeks" is one card with four
//    pips, never four cards. The table has no occurrence rows, so four cards
//    would be an invention with no id behind them.
//  - Three pip colours and no more: done, running, not-done. A fourth for "the
//    one coming next" was cut — the countdown directly above already says when,
//    and a fourth colour stops being a signal. Amber is an exception state
//    (backing off after a failure), not a fourth normal one.
//  - No progress bar. It encoded elapsed-wait-between-runs, which the countdown
//    said better, and its pink→orange fill implied the two ends meant different
//    things. They did not.
//  - No icons on the cards. The YOURS/AGENT pills carry ownership on their own.
//
// The popup reuses .waSheet (bottom sheet on a phone, centred card ≥641px),
// which already ships and is already tested on Julian's phone via the WhatsApp
// import flow.

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ArrowLeft, Plus, Clock } from "react-feather";

import { useSession } from "../store/session";
import ScheduleAccess from "./ScheduleAccess";
import SchedulePlans, { ScheduleChatButton } from "./SchedulePlans";
import type { Followup } from "../lib/types";

/** How often the list re-fetches while open. The scheduler ticks every 30s, so
 *  polling faster than that mostly re-renders identical rows. */
const POLL_MS = 15_000;

/* ---------------------------------------------------------------------------
   Formatting. All of it derives from the real columns — nothing here is
   invented, because a schedule screen that rounds or guesses is worse than none.
   --------------------------------------------------------------------------- */

/** "2d 15h" / "37m 04s" / "8s". Coarse above an hour on purpose: a countdown
 *  to next Saturday that ticks its seconds is noise. */
function humanGap(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const x = s % 60;
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${String(x).padStart(2, "0")}s`;
  return `${x}s`;
}

/** A gap as a cadence phrase: "Every 24h", "Every 14 days". */
function humanEvery(ms: number): string {
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `Every ${mins}m`;
  const hours = Math.round(ms / 3_600_000);
  if (hours < 48) return `Every ${hours}h`;
  return `Every ${Math.round(ms / 86_400_000)} days`;
}

function shortDate(epoch: number): string {
  return new Date(epoch).toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** How often, in words — the card's footer line. */
function cadenceOf(f: Followup): string {
  if (f.everyMs == null) return "Once";
  const base = humanEvery(f.everyMs);
  return f.backoff ? `${base}, then backing off` : base;
}

/** When does this STOP? The question a schedule raises, answered in words.
 *  Agent rows are required to carry a ceiling, so this is never empty for them. */
function ceilingOf(f: Followup): string {
  if (f.everyMs == null) return "Runs once, then it is done";
  const bits: string[] = [];
  if (f.maxRuns != null) bits.push(`stops after run ${f.maxRuns}`);
  if (f.deadlineAt != null) bits.push(`gives up ${shortDate(f.deadlineAt)}`);
  return bits.length ? bits.join(" · ").replace(/^./, (c) => c.toUpperCase()) : "No ceiling — repeats until you cancel it";
}

/** The next few fire times, projected forward from the row's own fields. Only
 *  as far as the ceiling allows, so we never promise runs that cannot happen. */
function nextRuns(f: Followup, howMany = 3): string[] {
  const out: string[] = [];
  let at = f.nextFireAt;
  let gap = f.everyMs;
  let run = f.runsDone;
  for (let i = 0; i < howMany; i++) {
    if (f.deadlineAt != null && at > f.deadlineAt) break;
    if (f.maxRuns != null && run >= f.maxRuns) break;
    const last = f.maxRuns != null && run + 1 >= f.maxRuns;
    out.push(shortDate(at) + (last ? ", final" : ""));
    if (gap == null) break;
    at += gap;
    run += 1;
    if (f.backoff) gap = Math.min(gap * 2, gap * 64);
  }
  return out;
}

const isLive = (f: Followup) => f.status === "armed";
const isTrouble = (f: Followup) => f.failCount > 0 && isLive(f);

/* ---------------------------------------------------------------------------
   Pips — "how many times", answered without a sentence.
   --------------------------------------------------------------------------- */
function Pips({ f }: { f: Followup }) {
  // A ONE-SHOT (everyMs null) is not unbounded — it is bounded at one. Checking
  // maxRuns first put every one-shot into the "repeats until you cancel it"
  // branch, so a finished single reminder sat under the word "Once" claiming it
  // repeated forever. Two labels on the same card contradicting each other.
  if (f.everyMs == null) {
    return (
      <div className="schedCard__pips">
        <i className={f.runsDone > 0 ? "is-done" : ""} />
        <b>{f.runsDone > 0 ? "Ran once" : "Runs once"}</b>
      </div>
    );
  }
  // Recurring with no maxRuns: nothing to count towards. Show the runs already
  // done and trail into words — a fixed row of dots would read as a total.
  if (f.maxRuns == null) {
    return (
      <div className="schedCard__pips">
        {Array.from({ length: Math.min(f.runsDone, 8) }, (_, i) => (
          <i key={i} className="is-done" />
        ))}
        <b>
          {f.runsDone
            ? `${f.runsDone} run${f.runsDone === 1 ? "" : "s"} so far · repeats until you cancel it`
            : "Repeats until you cancel it"}
        </b>
      </div>
    );
  }
  const live = isLive(f);
  return (
    <div className="schedCard__pips">
      {Array.from({ length: f.maxRuns }, (_, i) => {
        const cls =
          i < f.runsDone ? "is-done" : i === f.runsDone && live && isTrouble(f) ? "is-warn" : "";
        return <i key={i} className={cls} />;
      })}
      <b>{live ? `Run ${f.runsDone + 1} of ${f.maxRuns}` : `Ran ${f.runsDone} of ${f.maxRuns}`}</b>
    </div>
  );
}

/** YOURS / AGENT / RETRYING / the terminal status. The whole ownership signal. */
function Tag({ f }: { f: Followup }) {
  if (!isLive(f)) return <span className="schedTag schedTag--done">{f.status}</span>;
  if (isTrouble(f)) return <span className="schedTag schedTag--warn">Retrying</span>;
  return (
    <span className={`schedTag schedTag--${f.createdBy === "agent" ? "agent" : "you"}`}>
      {f.createdBy === "agent" ? "Agent" : "Yours"}
    </span>
  );
}

/* ---------------------------------------------------------------------------
   Card
   --------------------------------------------------------------------------- */
function Card({
  f,
  showCompany,
  companyName,
  now,
  onOpen,
}: {
  f: Followup;
  showCompany: boolean;
  companyName: string;
  now: number;
  onOpen: () => void;
}) {
  const live = isLive(f);
  const due = f.nextFireAt - now;
  // The row is due but has not been picked up yet: the tick runs every 30s, and
  // a gate (pause / mid-turn / no session headroom) can hold it longer. Saying
  // "0s" forever would look broken; this says what is actually true.
  const runningish = live && due <= 0;

  return (
    <div
      className={`schedCard ${live ? "" : "is-finished"}`}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      {showCompany && <span className="schedCard__chip">{companyName}</span>}
      <div className="schedCard__head">
        <h4 className="schedCard__title">{f.reason || f.prompt}</h4>
        <Tag f={f} />
      </div>
      <p className="schedCard__peek">{f.prompt}</p>
      <div
        className={`schedCard__when ${runningish ? "is-live" : isTrouble(f) ? "is-warn" : ""}`}
      >
        {!live ? (
          f.lastRunAt ? `Last ran ${shortDate(f.lastRunAt)}` : "Never ran"
        ) : runningish ? (
          <b>Waiting to run</b>
        ) : (
          <>
            Next run in <b>{humanGap(due)}</b>
          </>
        )}
      </div>
      <Pips f={f} />
      <div className="schedCard__foot">{cadenceOf(f)}</div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Detail sheet
   --------------------------------------------------------------------------- */
function Detail({
  f,
  companyName,
  now,
  onClose,
}: {
  f: Followup;
  companyName: string;
  now: number;
  onClose: () => void;
}) {
  const cancel = useSession((s) => s.cancelFollowup);
  const runNow = useSession((s) => s.runFollowupNow);
  const [confirming, setConfirming] = useState(false);
  const live = isLive(f);
  const due = f.nextFireAt - now;
  const runningish = live && due <= 0;

  return (
    <div className="schedDetail">
      <div className="schedDetail__head">
        <div className="schedDetail__headText">
          <div className="schedDetail__title">{f.reason || f.prompt.slice(0, 80)}</div>
          <div className="schedDetail__sub">{cadenceOf(f)}</div>
        </div>
        <Tag f={f} />
      </div>

      <ScheduleChatButton chatId={f.companyId} companyName={companyName} />

      {f.reason && f.createdBy === "agent" && (
        <div className="schedPanel schedPanel--why">
          <b>Why The Agent Armed It</b>
          {f.reason}
        </div>
      )}

      {isTrouble(f) && (
        <div className="schedPanel schedPanel--warn">
          <b>
            Failed {f.failCount}×, still armed
          </b>
          {f.lastError ?? "No detail recorded."}
          <br />
          Retries on a widening gap and gives up after 5 consecutive failures.
        </div>
      )}

      {live && (
        <div className={`schedDetail__when ${runningish ? "is-live" : isTrouble(f) ? "is-warn" : ""}`}>
          {runningish ? (
            <b>Waiting to run</b>
          ) : (
            <>
              Next run in <b>{humanGap(due)}</b>
            </>
          )}
        </div>
      )}

      <div className="schedDetail__label">What Arrives In The Chat</div>
      <div className="schedSaid">{f.prompt}</div>
      <p className="schedDetail__note">
        Sent verbatim, as if you had typed it. The turn that receives this has no memory of the
        conversation that armed it — everything it needs to act is in these words.
      </p>

      <div className="schedKv"><span>Cadence</span><span>{cadenceOf(f)}</span></div>
      <div className="schedKv"><span>Ceiling</span><span>{ceilingOf(f)}</span></div>
      <div className="schedKv">
        <span>Last run</span>
        <span>{f.lastRunAt ? shortDate(f.lastRunAt) : "Never run yet"}</span>
      </div>
      <div className="schedKv"><span>Lands in</span><span>{companyName}</span></div>
      <div className="schedKv">
        <span>Armed</span>
        <span>{shortDate(f.createdAt)}, by {f.createdBy === "agent" ? "the agent" : "you"}</span>
      </div>
      <div className="schedKv"><span>Reference</span><span><code>{f.id}</code></span></div>

      {live && nextRuns(f).length > 0 && (
        <>
          <div className="schedDetail__label">Next Runs</div>
          {nextRuns(f).map((n) => (
            <div className="schedKv" key={n}><span>{n}</span><span /></div>
          ))}
        </>
      )}

      {live && f.canCancel && !confirming && (
        <div className="schedDetail__acts">
          <button type="button" className="schedBtn" onClick={() => void runNow(f.id)}>
            Run it now
          </button>
          <button
            type="button"
            className="schedBtn schedBtn--danger"
            onClick={() => setConfirming(true)}
          >
            Cancel schedule
          </button>
        </div>
      )}

      {confirming && (
        <div className="schedPanel schedPanel--warn" style={{ marginTop: 24 }}>
          <b>Cancel this schedule?</b>
          It stops permanently
          {f.maxRuns != null ? `, with ${Math.max(0, f.maxRuns - f.runsDone)} runs left` : ""}.
          <div className="schedDetail__acts">
            <button type="button" className="schedBtn" onClick={() => setConfirming(false)}>
              Keep it
            </button>
            <button
              type="button"
              className="schedBtn schedBtn--danger"
              onClick={() => {
                setConfirming(false);
                void cancel(f.id);
                onClose();
              }}
            >
              Cancel it
            </button>
          </div>
        </div>
      )}

      {live && !f.canCancel && (
        <div className="schedPanel schedPanel--lock">
          The agent armed this to finish something it started, so it cannot be cancelled by hand.{" "}
          <b>Interrupt the chat and it cancels itself</b> — pressing Stop means you no longer want
          that work, and this only exists to finish it.
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
   New schedule.

   Deliberately four decisions, not eight. The spool accepts delay/every/backoff/
   max_runs/deadline, but an operator arming a reminder by hand is not writing a
   retry ladder — they are saying "say this to me then". Backoff and deadline are
   the agent's tools for polling something that may never arrive, and exposing
   them here would be four more controls to explain for a case nobody has.

   Everything is validated AGAIN on the server by the same validate() the spool
   uses, so the presets below are a convenience, never the guard.
   --------------------------------------------------------------------------- */
const WHEN_PRESETS: { label: string; ms: number }[] = [
  { label: "In 1 hour", ms: 3_600_000 },
  { label: "In 3 hours", ms: 10_800_000 },
  { label: "Tomorrow", ms: 86_400_000 },
  { label: "Next week", ms: 604_800_000 },
];
/** Sentinel for "custom", distinct from null (= don't repeat) and from any real gap. */
const CUSTOM = -1;
const REPEAT_PRESETS: { label: string; ms: number | null }[] = [
  { label: "Don't repeat", ms: null },
  { label: "Hourly", ms: 3_600_000 },
  { label: "Daily", ms: 86_400_000 },
  { label: "Weekly", ms: 604_800_000 },
  { label: "Custom", ms: CUSTOM },
];
const UNIT_MS = {
  minutes: 60_000,
  hours: 3_600_000,
  days: 86_400_000,
  weeks: 604_800_000,
} as const;

/** `datetime-local` wants local wall-clock, and toISOString is UTC — feeding it
 *  that shifts every default by the timezone offset. */
function localInputValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function NewSchedule({ companyId, companyName, onDone, onLeaveGuard }: {
  companyId: string;
  companyName: string;
  onDone: () => void;
  onLeaveGuard: (guard: (() => boolean) | null) => void;
}) {
  const create = useSession((s) => s.createFollowup);
  const [prompt, setPrompt] = useState("");
  const [delayMs, setDelayMs] = useState(WHEN_PRESETS[0]!.ms);
  const [everyMs, setEveryMs] = useState<number | null>(null);
  const [maxRuns, setMaxRuns] = useState(4);
  const [busy, setBusy] = useState(false);

  // Custom when: an exact wall-clock moment, not an offset. "Next Tuesday 09:00"
  // is a thing people want and no preset can express.
  const [customAt, setCustomAt] = useState("");
  // Custom repeat: any number of any unit. The presets are the common cases,
  // not the ceiling of what the table can hold.
  const [everyN, setEveryN] = useState(2);
  const [everyUnit, setEveryUnit] = useState<keyof typeof UNIT_MS>("hours");
  const customEveryMs = everyN * UNIT_MS[everyUnit];

  const whenCustom = delayMs < 0;
  const repeatCustom = everyMs === CUSTOM;

  // One place computes the real numbers, so what the summary shows and what the
  // request sends can never disagree.
  const effectiveDelay = whenCustom
    ? customAt
      ? new Date(customAt).getTime() - Date.now()
      : NaN
    : delayMs;
  const effectiveEvery = repeatCustom ? customEveryMs : everyMs;

  const fires = new Date(Date.now() + (Number.isFinite(effectiveDelay) ? effectiveDelay : 0));
  const whenBad =
    !Number.isFinite(effectiveDelay) || effectiveDelay < 10_000
      ? whenCustom && !customAt
        ? "Pick a date and time."
        : "That time has already passed — pick one at least a few seconds out."
      : null;
  const everyBad =
    effectiveEvery != null && effectiveEvery < 60_000 ? "A repeat cannot be tighter than a minute." : null;
  const ready = prompt.trim().length > 0 && !whenBad && !everyBad && !busy;
  const dirty = !!(prompt || customAt || everyMs != null || delayMs !== WHEN_PRESETS[0]!.ms);
  useEffect(() => {
    onLeaveGuard(() => !busy && (!dirty || window.confirm("Discard your unsaved schedule changes?")));
    const unload = (e: BeforeUnloadEvent) => { if (dirty || busy) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", unload);
    return () => { onLeaveGuard(null); window.removeEventListener("beforeunload", unload); };
  }, [busy, dirty, onLeaveGuard]);

  return (
    <div className="schedNew" data-chat-unsaved={dirty ? "true" : undefined} data-chat-busy={busy ? "true" : undefined}>
      <div className="schedDetail__label">What Should Arrive In The Chat</div>
      <textarea
        className="schedInput"
        rows={4}
        autoFocus
        aria-label="Scheduled message"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="Chase the cci.sg invoice — check whether it was paid first."
      />
      <p className="schedDetail__note">
        This is sent verbatim, as if you had typed it. The turn that receives it has no memory of
        right now, so say what to check and what done looks like.
      </p>

      <div className="schedDetail__label">When</div>
      <div className="schedChips">
        {WHEN_PRESETS.map((w) => (
          <button
            key={w.label}
            type="button"
            className={`schedChip ${delayMs === w.ms ? "is-active" : ""}`}
            onClick={() => setDelayMs(w.ms)}
          >
            {w.label}
          </button>
        ))}
        <button
          type="button"
          className={`schedChip ${whenCustom ? "is-active" : ""}`}
          onClick={() => {
            setDelayMs(-1);
            if (!customAt) setCustomAt(localInputValue(new Date(Date.now() + 3_600_000)));
          }}
        >
          Pick a time
        </button>
      </div>
      {whenCustom && (
        <input
          type="datetime-local"
          className="schedInput schedInput--line"
          value={customAt}
          min={localInputValue(new Date(Date.now() + 60_000))}
          onChange={(e) => setCustomAt(e.target.value)}
        />
      )}

      <div className="schedDetail__label">Repeat</div>
      <div className="schedChips">
        {REPEAT_PRESETS.map((r) => (
          <button
            key={r.label}
            type="button"
            className={`schedChip ${everyMs === r.ms ? "is-active" : ""}`}
            onClick={() => setEveryMs(r.ms)}
          >
            {r.label}
          </button>
        ))}
      </div>
      {repeatCustom && (
        <div className="schedEvery">
          <span>Every</span>
          <input
            type="number"
            min={1}
            max={999}
            className="schedInput schedInput--num"
            value={everyN}
            onChange={(e) => setEveryN(Math.max(1, Number(e.target.value) || 1))}
          />
          <div className="schedChips">
            {(Object.keys(UNIT_MS) as (keyof typeof UNIT_MS)[]).map((u) => (
              <button
                key={u}
                type="button"
                className={`schedChip ${everyUnit === u ? "is-active" : ""}`}
                onClick={() => setEveryUnit(u)}
              >
                {u}
              </button>
            ))}
          </div>
        </div>
      )}

      {effectiveEvery != null && (
        <>
          {/* A repeat with no ceiling bills real money forever. The agent is
              REFUSED one by validate(); an operator gets one by default and has
              to opt out on purpose. */}
          <div className="schedDetail__label">Stop After</div>
          <div className="schedChips">
            {[2, 4, 8, 12].map((n) => (
              <button
                key={n}
                type="button"
                className={`schedChip ${maxRuns === n ? "is-active" : ""}`}
                onClick={() => setMaxRuns(n)}
              >
                {n} runs
              </button>
            ))}
            <button
              type="button"
              className={`schedChip ${maxRuns === 0 ? "is-active" : ""}`}
              onClick={() => setMaxRuns(0)}
            >
              Never
            </button>
          </div>
        </>
      )}

      <div className="schedKv" style={{ marginTop: 22 }}>
        <span>First run</span>
        <span>{whenBad ? "—" : shortDate(fires.getTime())}</span>
      </div>
      {effectiveEvery != null && !everyBad && (
        <div className="schedKv">
          <span>Then</span>
          <span>{humanEvery(effectiveEvery)}{maxRuns > 0 ? ` · stops after ${maxRuns}` : " · no ceiling"}</span>
        </div>
      )}
      <div className="schedKv">
        <span>Lands in</span>
        <span>{companyName}</span>
      </div>
      {/* A disabled button with no reason is a dead end. Say which field is wrong. */}
      {(whenBad || everyBad) && prompt.trim().length > 0 && (
        <p className="schedWhy">{whenBad ?? everyBad}</p>
      )}

      <div className="schedDetail__acts">
        <button type="button" className="schedBtn" onClick={() => { if (!dirty || window.confirm("Discard your unsaved schedule changes?")) onDone(); }} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="schedBtn schedBtn--go"
          disabled={!ready}
          onClick={async () => {
            setBusy(true);
            const ok = await create({
              companyId,
              prompt: prompt.trim(),
              delayMs: Math.round(effectiveDelay),
              everyMs: effectiveEvery,
              maxRuns: effectiveEvery != null && maxRuns > 0 ? maxRuns : null,
            });
            setBusy(false);
            if (ok) onDone();
          }}
        >
          {busy ? "Arming…" : "Schedule it"}
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
   The screen
   --------------------------------------------------------------------------- */
export default function ScheduledTasks() {
  const close = useSession((s) => s.closeScheduled);
  const scope = useSession((s) => s.scheduledScope);
  const setScope = useSession((s) => s.setScheduledScope);
  const rows = useSession((s) => s.followups);
  const names = useSession((s) => s.followupNames);
  const loading = useSession((s) => s.followupsLoading);
  const error = useSession((s) => s.followupsError);
  const totals = useSession((s) => s.followupTotals);
  const fetchFollowups = useSession((s) => s.fetchFollowups);
  const companies = useSession((s) => s.companies);
  const clearFinished = useSession((s) => s.clearFinishedFollowups);
  const activeCompanyId = useSession((s) => s.activeCompanyId);

  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [planCount, setPlanCount] = useState<number | null>(null);
  const [planTotals, setPlanTotals] = useState({ chat: 0, all: 0 });
  const [openPlanId, setOpenPlanId] = useState<string | null>(null);
  const leaveGuard = useRef<(() => boolean) | null>(null);
  const registerGuard = useMemo(() => (guard: (() => boolean) | null) => { leaveGuard.current = guard; }, []);
  const bodyRef = useRef<HTMLDivElement>(null);
  const canLeave = () => !leaveGuard.current || leaveGuard.current();
  const requestClose = () => { if (canLeave()) close(); };
  const goBack = () => { if (!canLeave()) return; if (creating) setCreating(false); else if (openPlanId) setOpenPlanId(null); else setOpenId(null); };
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = 0; }, [openId, openPlanId, creating]);

  // One ticker for every countdown, so they stay in step — two cards showing
  // different seconds looks broken.
  //
  // But it only needs to be a SECOND ticker when a second is actually visible.
  // humanGap switches to "2d 15h" above an hour, so below-the-hour rows are the
  // only ones whose text changes each second; when nothing is that close, a
  // 1Hz re-render of the whole list is pure cost, and on a phone it is the
  // difference between smooth and not.
  const [now, setNow] = useState(() => Date.now());
  const soon = rows.some((f) => isLive(f) && f.nextFireAt - now < 3_600_000);
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), soon ? 1000 : 20_000);
    return () => window.clearInterval(id);
  }, [soon]);

  // Poll while mounted. The ref keeps the interval from being torn down and
  // rebuilt every time the action identity changes.
  const fetchRef = useRef(fetchFollowups);
  fetchRef.current = fetchFollowups;
  useEffect(() => {
    const id = window.setInterval(() => void fetchRef.current(), POLL_MS);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (leaveGuard.current && !leaveGuard.current()) return;
      if (creating) setCreating(false);
      else if (openPlanId) setOpenPlanId(null);
      else if (openId) setOpenId(null);
      else close();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [close, openId, openPlanId, creating]);

  const nameOf = (cid: string) =>
    names[cid] ?? companies.find((c) => c.id === cid)?.name ?? cid;

  const groups = useMemo(() => {
    const mine = rows.filter((f) => isLive(f) && f.createdBy !== "agent");
    const theirs = rows.filter((f) => isLive(f) && f.createdBy === "agent");
    // Finished rows age out of the LIST after a day. They stay in the table —
    // pruneFollowups keeps them 30 days as history — but a schedule you dealt
    // with yesterday is not something you need to scroll past today, and the
    // Finished pile was already burying the two rows that still matter.
    const cutoff = Date.now() - 86_400_000;
    const done = rows.filter((f) => !isLive(f) && (f.lastRunAt ?? f.createdAt) > cutoff);
    return [
      { key: "mine", label: "Yours", items: mine },
      { key: "theirs", label: "The Agent's Own", items: theirs },
      { key: "done", label: "Finished", items: done },
    ].filter((g) => g.items.length > 0);
  }, [rows]);

  const open = openId ? rows.find((f) => f.id === openId) ?? null : null;

  return createPortal(
    <div
      className="waSheet schedSheet"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Scheduled tasks"
    >
      <div className="waSheet__panel">
        <div className="waSheet__handle" />
        <header className="schedSheet__header">
          {(creating || open || openPlanId) && (
            <button type="button" className="schedSheet__back" onClick={goBack} aria-label="Back to all schedules">
              <ArrowLeft width={17} height={17} />
            </button>
          )}
          <h2 className="schedSheet__h1">
            {creating ? "Create manually" : (open || openPlanId) ? "Scheduled Task" : "Scheduled Tasks"}
          </h2>
          <button type="button" className="schedSheet__x" onClick={requestClose} aria-label="Close">
            <X width={17} height={17} />
          </button>
        </header>
        <div className="waSheet__body" ref={bodyRef}>
          {creating ? (
            <>
              <NewSchedule
                companyId={activeCompanyId!}
                companyName={nameOf(activeCompanyId!)}
                onDone={() => setCreating(false)}
                onLeaveGuard={registerGuard}
              />
            </>
          ) : open ? (
            <>
              <Detail
                f={open}
                companyName={nameOf(open.companyId)}
                now={now}
                onClose={() => setOpenId(null)}
              />
            </>
          ) : (
            <>

              {/* Both counts, always. Showing only the selected side's number
                  means the count you would use to decide whether to switch is
                  the one you cannot see until after you have switched. */}
              <div hidden={!!openPlanId}>
              <div className="schedScope" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={scope === "chat"}
                  className={scope === "chat" ? "is-active" : ""}
                  onClick={() => setScope("chat")}
                >
                  This Chat
                  <span className="schedScope__n">{loading || planCount === null ? "…" : totals.chat + planTotals.chat}</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={scope === "all"}
                  className={scope === "all" ? "is-active" : ""}
                  onClick={() => setScope("all")}
                >
                  All Chats
                  <span className="schedScope__n">{loading || planCount === null ? "…" : totals.all + planTotals.all}</span>
                </button>
              </div>

              {activeCompanyId && scope === "chat" && <ScheduleAccess chatId={activeCompanyId} chatName={nameOf(activeCompanyId)}
                isCurrentChat={() => useSession.getState().activeCompanyId === activeCompanyId && useSession.getState().scheduledScope === "chat"} />}

              </div>
              <SchedulePlans selectedId={openPlanId} onSelect={setOpenPlanId} onLeaveGuard={registerGuard} hideEmpty scope={scope} chatId={activeCompanyId} nameOf={nameOf} onCount={(count, counts) => { setPlanCount(count); setPlanTotals(counts ?? { chat: 0, all: 0 }); }}
                isCurrentView={() => useSession.getState().activeCompanyId === activeCompanyId && useSession.getState().scheduledScope === scope} />

              <div hidden={!!openPlanId}>
              {error && <div className="schedEmpty schedEmpty--error">{error}</div>}

              {!error && !loading && rows.length === 0 && planCount === 0 && (
                <div className="schedEmpty" role="status">
                  <div className="schedEmpty__mark" aria-hidden="true"><Clock width={26} height={26} /></div>
                  <h3 className="schedEmpty__title">No tasks scheduled yet</h3>
                  <p className="schedEmpty__hint">
                    {scope === "chat"
                      ? "A connection isn’t a task. After you describe what and when, ask your agent to save it. Your scheduled tasks will appear here."
                      : "No saved tasks across your chats. Choose This Chat to connect an agent and describe a schedule."}
                  </p>
                </div>
              )}

              {groups.map((g) => (
                <div key={g.key}>
                  <div className="schedGroup schedGroup--row">
                    <span>{g.label}</span>
                    {g.key === "done" && (
                      <button type="button" className="schedClear" onClick={() => void clearFinished()}>
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="schedGrid">
                    {g.items.map((f) => (
                      <Card
                        key={f.id}
                        f={f}
                        showCompany={scope === "all"}
                        companyName={nameOf(f.companyId)}
                        now={now}
                        onOpen={() => setOpenId(f.id)}
                      />
                    ))}
                  </div>
                </div>
              ))}
              {activeCompanyId && <button type="button" className="schedule-agent__link schedule-agent__manual"
                title={`Create a task for ${nameOf(activeCompanyId)}`} onClick={() => setCreating(true)}>
                <Plus size={16} aria-hidden="true" /> Create manually
              </button>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
